import React, {useContext, useEffect, useState} from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';

import {useStyles} from "./PlayerDashboardSection.css";
import {AppbarContentContext} from "../../../../../commons/routing/appbar/AppbarContentProvider";
import {FormattedMessage, useIntl} from 'react-intl';
import useFetchPlayerDashboardData from "./useFetchPlayerDashboardData";
import LoadingProgressIndication from "../../../../../commons/elements/progress/loading/LoadingProgressIndication";
import {IconButton, MenuItem, Select, Typography} from '@mui/material';
import FormControl from "@mui/material/FormControl";
import noDashboardData from 'assets/noDashboardData.svg';
import filterCoursePlaylist from 'assets/undraw_filter_course_playlist.svg';
import wellDone from 'assets/undraw_well_done.svg';
import examPlaylist from 'assets/undraw_exams.svg';
import noCoursesYet from 'assets/undraw_graduation.svg';
import {DateRangeSharp} from "@mui/icons-material";
import StatisticsDateRangePicker from "../../../statistics/utils/StatisticsDateRangePicker";
import {useConfirm} from "material-ui-confirm";
import {subDays} from 'date-fns';
import BasicChart from "../../../statistics/chart/BasicChart";
import ScoredScenesPerformanceList from "../../panels/performanceList/ScoredScenesPerformanceList";
import ValueIndicator from "../../panels/valueIndication/ValueIndicator";

export default function PlayerDashboardSection() {
    const classes = useStyles();

    // Setting the current appbar content
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    // Confirmation dialog for selecting the time range
    const confirm = useConfirm();

    // Internationalization for simple strings
    const intl = useIntl();

    // Fetching the data required for rendering the statistics and retrieving selectable course/playlists
    const {fetchPlayerDashboardData, selectableFilterHierarchy, noSubscribedCourses} = useFetchPlayerDashboardData()

    // Filter
    const [associationFilter, setAssociationFilter] = useState('')
    const [courseFilter, setCourseFilter] = useState('')
    const [playlistFilter, setPlaylistFilter] = useState('')
    const [fromToDateRange, setFromToDateRange] = React.useState([subDays(new Date(), 365), new Date()])

    // Raw dashboard data object as basis to render charts and numbers
    const [dashboardData, setDashboardData] = useState({})

    // Loading indication - set to false in case all charts are ready
    const [loadingStatistics, setLoadingStatistics] = useState(true)

    // Setting the appbar content including the filters
    useEffect(() => {
        setCurrentAppbarContent({
            header: <FormattedMessage id={'app.dashboard.overview.appbar.header'}
                                      defaultMessage={'Dashboard'}/>,
            description: <FormattedMessage id={'app.dashboard.overview.appbar.description'}
                                           defaultMessage={'All important KPIs at one glance...'}/>,
            actions: <>
                {getFilterSelection()}
            </>
        })
    }, [selectableFilterHierarchy, associationFilter, courseFilter, playlistFilter, fromToDateRange])

    // Set a "default" selected association and load the dashboard data for the first time
    useEffect(() => {
        if (selectableFilterHierarchy.length > 0) {
            setAssociationFilter(selectableFilterHierarchy[0].key)

            /* Reloading the dashboard for the first time - in order to avoid double-requests caused by multiple state changes,
               reloads on field changes are handled directly in the respective component's handler. */
            reloadDashboardData(selectableFilterHierarchy[0].key, courseFilter, playlistFilter, fromToDateRange[0], fromToDateRange[1])
        }
    }, [selectableFilterHierarchy])

    // Change of association resets course and playlist filter
    useEffect(() => {
        setCourseFilter('') // All
        setPlaylistFilter('') // All
    }, [selectableFilterHierarchy, associationFilter])

    // Change of course resets playlist filter
    useEffect(() => {
        setPlaylistFilter('') // All
    }, [selectableFilterHierarchy, associationFilter, courseFilter])

    const getSelectableCourses = () => {

        // Get courses in sorted order (as the courses array is pushed in arbitrary order by MongoDB)
        return selectableFilterHierarchy.find((a) => a.key === associationFilter)?.courses?.sort((c1, c2) => {
            if (c1.name < c2.name) {
                return -1
            }

            if (c1.name > c2.name) {
                return 1
            }

            return 0
        }) || []
    }

    const getSelectablePlaylists = () => {

        // Course filter is empty (i.e. "All Courses") - get playlists of all courses of the selected association
        if (courseFilter === '') {
            const playlists = []
            selectableFilterHierarchy.find((a) => a.key === associationFilter)?.courses?.forEach((c) => {
                c.playlists?.forEach((p) => {
                    if (!playlists.some((alreadyAddedPlaylist) => alreadyAddedPlaylist.key === p.key)) {
                        playlists.push(p)
                    }
                })
            })

            return playlists
        }

        // Only get the playlist of the selected course
        return selectableFilterHierarchy.find((a) => a.key === associationFilter)?.courses.find((c) => c.key === courseFilter)?.playlists || []
    }

    // Query statistical data and fill the chart canvas accordingly
    const reloadDashboardData = async (association, course, playlist, fromDate, toDate) => {
        setLoadingStatistics(true)

        // Load the basic statistical data as a basis for feeding the charts in the dashboard
        const dashboardData = await fetchPlayerDashboardData(association, course, playlist, fromDate, toDate)

        // Set the dashboard data state, so that it can be used by the charts - go sure that the dashboard data is never set to null to avoid object access problems
        setDashboardData(dashboardData || {})

        setLoadingStatistics(false)
    }

    const getFilterSelection = () => {

        // Hide dropdown in case the selectable items are not yet loaded
        if (!(selectableFilterHierarchy.length > 0)) {
            return <></>
        }

        // Filter row
        return <div className={classes.filterRow}>

            {/* Association */}
            <FormControl className={classes.formControl}>
                <Select id="reg-association-input"
                        style={{color: "white", borderRadius: 30, borderColor: "grey"}}
                        variant="outlined"
                        value={associationFilter || ''}
                        onChange={(event) => {
                            setAssociationFilter(event.target.value)
                            reloadDashboardData(event.target.value, '', '', fromToDateRange[0], fromToDateRange[1])
                        }}>

                    {selectableFilterHierarchy?.map((association) =>
                        <MenuItem key={association.key} value={association.key}>
                            {intl.formatMessage({
                                id: `app.ids.association.${association.key}`,
                                defaultMessage: association.key
                            })}
                        </MenuItem>)}
                </Select>
            </FormControl>

            {/* Course */}
            <FormControl className={classes.formControl}>
                <Select id="reg-course-input"
                        style={{color: "white", borderRadius: 30, borderColor: "grey"}}
                        variant="outlined"
                        displayEmpty={true}
                        value={courseFilter || ''}
                        onChange={(event) => {
                            setCourseFilter(event.target.value)
                            reloadDashboardData(associationFilter, event.target.value, '', fromToDateRange[0], fromToDateRange[1])
                        }}>

                    <MenuItem value="">
                        <em>All Courses</em>
                    </MenuItem>

                    {getSelectableCourses()?.map((course) =>
                        <MenuItem key={course.key} value={course.key}>
                            {course.name}
                        </MenuItem>)}
                </Select>
            </FormControl>

            {/* Playlist */}
            <FormControl className={classes.formControl}>
                <Select id="reg-playlist-input"
                        style={{color: "white", borderRadius: 30, borderColor: "grey"}}
                        variant="outlined"
                        displayEmpty={true}
                        value={playlistFilter || ''}
                        onChange={(event) => {
                            setPlaylistFilter(event.target.value)
                            reloadDashboardData(associationFilter, courseFilter, event.target.value, fromToDateRange[0], fromToDateRange[1])
                        }}>

                    <MenuItem value="">
                        <em>All Playlists</em>
                    </MenuItem>

                    {getSelectablePlaylists()?.map((playlist) =>
                        <MenuItem key={playlist.key} value={playlist.key}>
                            {playlist.name}
                        </MenuItem>)}
                </Select>
            </FormControl>

            {/* Date range */}
            <div style={{marginLeft: 10}}>
                <b>{intl.formatDate(fromToDateRange[0]) + ' - ' + intl.formatDate(fromToDateRange[1])}</b>
                <IconButton onClick={handlePickDate} size="large">
                    <DateRangeSharp/>
                </IconButton>
            </div>

        </div>
    }

    // Selection of time range
    const handlePickDate = () => {
        let selectedFromDate
        let selectedToDate

        confirm({
            title: intl.formatMessage({
                id: 'app.dashboard.dialog.dateRange.selection.title',
                defaultMessage: 'Choose time range...'
            }),
            content: <StatisticsDateRangePicker
                startDate={fromToDateRange[0]}
                endDate={fromToDateRange[1]}
                onPeriodChanged={(startDate, endDate) => {
                    selectedFromDate = startDate
                    selectedToDate = endDate
                }
                }/>
        }).then(() => {

            // IMPORTANT: When selecting nothing, but clicking simply on OK, selectedStartDate and selectedEndDate is undefined
            // IMPORTANT: the timestamps are always at "start date 00:00:00 UTC" - "end date 23:59:59 UTC"
            if (selectedFromDate && selectedToDate) {
                setFromToDateRange([selectedFromDate, selectedToDate])

                reloadDashboardData(associationFilter, courseFilter, playlistFilter, selectedFromDate, selectedToDate)
            }
        }).catch(() => null)
    }

    // Source: Idea from https://stackoverflow.com/questions/36098913/convert-seconds-to-days-hours-minutes-and-seconds
    const formatDuration = (seconds) => {
        const h = Math.floor(seconds / 3600)
        const m = Math.floor(seconds % 3600 / 60)
        const s = Math.floor(seconds % 3600 % 60)

        const hh = h > 0 ? (h > 9 ? h : ("0" + h)) : "00"
        const mm = m > 0 ? (m > 9 ? m : ("0" + m)) : "00"
        const ss = s > 0 ? (s > 9 ? s : ("0" + s)) : "00"
        return `${hh}:${mm}:${ss}`
    }

    // Conditionally returns replayable scored scenes respectively related hints
    const getReplayableScoredScenesSection = () => {

        // Only show replayable scenes in case a concrete course and playlist is selected
        if (courseFilter && playlistFilter) {

            if (getSelectablePlaylists().find((p) => p.key === playlistFilter)?.feedbackMode === 'exam') {
                return <div className={classes.smallPanelHeader}>
                    <div>
                        {intl.formatMessage({
                            id: 'app.dashboard.overview.replayScoredScenes.info.noReplayForExam',
                            defaultMessage: 'Exam playlists cannot be replayed'
                        })}
                    </div>
                    <div>
                        <img className={classes.image} src={examPlaylist}
                             alt='No replay for exam playlists'/>
                    </div>
                </div>
            }

            // Avoid showing an empty section
            if (dashboardData.listReplayableScoredScenesPreview?.length > 0) {

                return <ScoredScenesPerformanceList
                    metric="decisionAccuracy"
                    unit="%"
                    headerPreview={intl.formatMessage({
                        id: 'app.dashboard.overview.replayScoredScenes.header',
                        defaultMessage: 'Replay Wrong-Judged Scenes'
                    })}
                    listPreview={dashboardData.listReplayableScoredScenesPreview}
                    headerMore={intl.formatMessage({
                        id: 'app.dashboard.dialog.dateRange.replayScoredScenes.title',
                        defaultMessage: 'Replay Low-Rated Scenes'
                    })}
                    listMore={dashboardData.listReplayableScoredScenes}
                    overwriteLinkPath="/game/session"

                    // The additional parameter scoredSceneId is injected in the link directly in the component
                    overwriteLinkState={{
                        playlistName: getSelectablePlaylists().find((p) => p.key === playlistFilter)?.name,
                        scoredSceneCount: 1,
                        courseId: courseFilter,
                        playlistId: playlistFilter,
                        feedbackMode: getSelectablePlaylists().find((p) => p.key === playlistFilter)?.feedbackMode,
                        isReplay: true
                    }}/>

            } else {
                return <div className={classes.smallPanelHeader}>
                    <div>
                        {intl.formatMessage({
                            id: 'app.dashboard.overview.replayScoredScenes.info.noReplayableScenes',
                            defaultMessage: 'Well done - No scenes with 0% decision accuracy!'
                        })}
                    </div>
                    <div>
                        <img className={classes.image} src={wellDone}
                             alt='No replayable video scenes'/>
                    </div>
                </div>
            }
        }

        // Hint the user to filter for course and playlist first
        return <div className={classes.smallPanelHeader}>
            <div>
                {intl.formatMessage({
                    id: 'app.dashboard.overview.replayScoredScenes.info.incompleteSelection',
                    defaultMessage: 'Filter for course & playlist to replay scenes'
                })}
            </div>
            <div>
                <img className={classes.image} src={filterCoursePlaylist}
                     alt='Filter for course playlist first'/>
            </div>
        </div>
    }

    return (
        <div className={classes.root}>

            {/* Only hide the loading indication, so that the canvas-elements are always available in the DOM */}
            <div hidden={!loadingStatistics || noSubscribedCourses}>
                <LoadingProgressIndication text={intl.formatMessage({
                    id: 'app.dashboard.loading',
                    defaultMessage: 'Loading Dashboard...'
                })}/>
            </div>

            {/* Hide complete section while loading and in case no statistics are generated yet */}
            <Grid container spacing={2}
                  sx={(loadingStatistics || dashboardData.noDataYet || noSubscribedCourses) && {display: 'none'}}>

                {/* Overall indicators */}
                <Grid item xs={4}>
                    <ValueIndicator
                        size="big"
                        header={intl.formatMessage({
                            id: 'app.dashboard.overview.total.decisionAccuracy',
                            defaultMessage: 'Correct Decisions'
                        })}
                        value={`${dashboardData.numberDecisionAccuracyTotal}%`}/>
                </Grid>
                <Grid item xs={4}>
                    <ValueIndicator
                        size="big"
                        header={intl.formatMessage({
                            id: 'app.dashboard.overview.total.reactionTime',
                            defaultMessage: 'Reaction Time'
                        })}
                        value={dashboardData.numberReactionTimeTotal
                            ? `${dashboardData.numberReactionTimeTotal}s`
                            : '-'}/>
                </Grid>
                <Grid item xs={4}>
                    <ValueIndicator
                        size="big"
                        header={intl.formatMessage({
                            id: 'app.dashboard.overview.total.trainingIntensity',
                            defaultMessage: 'Training Intensity'
                        })}
                        value={formatDuration(dashboardData.numberTrainingIntensityTotal * 60)}/>
                </Grid>

                {/* Average bar charts */}
                <Grid item xs={4}>
                    <BasicChart chartId="chartDecisionAccuracyAvgByDisciplines"
                                chartData={dashboardData.chartDecisionAccuracyAvg}/>
                </Grid>
                <Grid item xs={4}>
                    <BasicChart chartId="chartReactionTimeAvgByDisciplines"
                                chartData={dashboardData.chartReactionTimeAvg}/>
                </Grid>
                <Grid item xs={4}>
                    <BasicChart chartId="chartTrainingIntensityAbsByDisciplines"
                                chartData={dashboardData.chartTrainingIntensityAbs}/>
                </Grid>

                {/* Replay scenes (only replayable in case a concrete course and playlist is selected - as this info is required) */}
                <Grid item xs={6}>
                    <Paper className={classes.groupPaperZeroDecisionAccuracy}>
                        {getReplayableScoredScenesSection()}
                    </Paper>

                </Grid>

                {/* Comparison with other users + ranking */}
                <Grid item xs={6}>

                    <Grid container spacing={2}>

                        {/* Leader board (values of the best performing judge) */}
                        <Grid item xs={12}>
                            <Paper className={classes.groupPaper}>

                                {/* Header */}
                                <div className={classes.smallPanelHeader}>
                                    {intl.formatMessage({
                                        id: 'app.dashboard.overview.leaderBoard.header',
                                        defaultMessage: 'Leader board'
                                    })}
                                </div>

                                {/* No user has enough decisions to show a reasonable leader board - this is indicated by the value null */}
                                {!dashboardData.numberDecisionAccuracyTotalBestPerformingJudge &&
                                <div className={classes.centeredText}>
                                    {intl.formatMessage({
                                        id: 'app.dashboard.overview.leaderBoard.notEnoughData',
                                        defaultMessage: 'Too little activity to show leader board.'
                                    })}
                                </div>}

                                {/* Decision accuracy of best performing judge */}
                                {dashboardData.numberDecisionAccuracyTotalBestPerformingJudge && <ValueIndicator
                                    highlighted={true}
                                    size="small"
                                    header={intl.formatMessage({
                                        id: 'app.dashboard.overview.leaderBoard.metric.decisionAccuracy',
                                        defaultMessage: 'Correct Decisions'
                                    })}
                                    value={`${dashboardData.numberDecisionAccuracyTotalBestPerformingJudge}%`}/>}

                                {/* Reaction time of best performing judge */}
                                {dashboardData.numberDecisionAccuracyTotalBestPerformingJudge && <ValueIndicator
                                    size="small"
                                    header={intl.formatMessage({
                                        id: 'app.dashboard.overview.leaderBoard.metric.reactionTime',
                                        defaultMessage: 'Reaction Time'
                                    })}
                                    value={dashboardData.numberReactionTimeTotalBestPerformingJudge
                                        ? `${dashboardData.numberReactionTimeTotalBestPerformingJudge}s`
                                        : '-'}/>}
                            </Paper>
                        </Grid>

                        {/* Own ranking among all users acc. to the selected filter */}
                        <Grid item xs={6}>
                            <Paper className={classes.groupPaperCentered}>

                                {/* Header */}
                                <div className={classes.smallPanelHeader}>
                                    {intl.formatMessage({
                                        id: 'app.dashboard.overview.ownRank.header',
                                        defaultMessage: 'Your Rank'
                                    })}
                                </div>

                                {!dashboardData.ownRanking && <div className={classes.centeredText}>
                                    {intl.formatMessage({
                                        id: 'app.dashboard.overview.ownRank.notEnoughData',
                                        defaultMessage: 'Too little activity to determine rank.'
                                    })}
                                </div>}

                                {/* Current ranking medal or a question mark in case the signed-in user has too little activity (i.e. "ownRanking" is null) */}
                                <div className={classes.medal}>
                                    <div className={classes.medalText}>
                                        {dashboardData.ownRanking || '?'}
                                    </div>
                                </div>
                            </Paper>
                        </Grid>

                        {/* Average performance of all users acc. to the selected filter */}
                        <Grid item xs={6}>
                            <Paper className={classes.groupPaper}>

                                {/* Header */}
                                <div className={classes.smallPanelHeader}>
                                    {intl.formatMessage({
                                        id: 'app.dashboard.overview.averagePerformanceAllUsers.header',
                                        defaultMessage: 'All Users Avg.'
                                    })}
                                </div>

                                {/* Decision accuracy of all users */}
                                <ValueIndicator
                                    highlighted={true}
                                    size="small"
                                    header={intl.formatMessage({
                                        id: 'app.dashboard.overview.averagePerformanceAllUsers.metric.decisionAccuracy',
                                        defaultMessage: 'Correct Decisions'
                                    })}
                                    value={`${dashboardData.numberDecisionAccuracyTotalAllUsers}%`}/>

                                {/* Reaction time of all users */}
                                <ValueIndicator
                                    size="small"
                                    header={intl.formatMessage({
                                        id: 'app.dashboard.overview.averagePerformanceAllUsers.metric.reactionTime',
                                        defaultMessage: 'Reaction Time'
                                    })}
                                    value={dashboardData.numberReactionTimeTotalAllUsers
                                        ? `${dashboardData.numberReactionTimeTotalAllUsers}s`
                                        : '-'}/>
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>

            </Grid>

            {/* In case no statistical data has been generated yet - show indication with nice cartoon image */}
            <div hidden={loadingStatistics || !dashboardData.noDataYet}>
                <Typography component="h1" variant="h4" textAlign="center">
                    {intl.formatMessage({
                        id: 'app.dashboard.overview.noData.header',
                        defaultMessage: 'Missing data to render dashboard...'
                    })}
                </Typography>
                <img className={classes.image} src={noDashboardData} alt='No dashboard data'/>
                <Typography component="h1" variant="h5" textAlign="center">
                    {intl.formatMessage({
                        id: 'app.dashboard.overview.noData.description',
                        defaultMessage: 'No activity for the requested filter criteria.'
                    })}
                </Typography>
            </div>

            {/* In case the user is not assigned to any course - should actually never happen as soon as open courses are configured.
                Consider: loading is still set to true in this case, but the loading indication is hidden in case of noSubscribedCourses. */}
            <div hidden={!noSubscribedCourses}>
                <Typography component="h1" variant="h4" textAlign="center">
                    {intl.formatMessage({
                        id: 'app.dashboard.overview.noSubscribedCourses.header',
                        defaultMessage: 'Not assigned to any course yet...'
                    })}
                </Typography>
                <img className={classes.image} src={noCoursesYet} alt='No course subscriptions yet'/>
                <Typography component="h1" variant="h5" textAlign="center">
                    {intl.formatMessage({
                        id: 'app.dashboard.overview.noSubscribedCourses.description',
                        defaultMessage: 'Contact your course organizer.'
                    })}
                </Typography>
            </div>
        </div>
    );
}
